import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PrimaryButton } from '../common/PrimaryButton';
import { SecondaryButton } from '../common/SecondaryButton';

interface EntityDefinition {
  name: string;
  displayName?: string;
  description?: string;
  trackFirstLast?: boolean;
  displayOrder?: number;
  showInTable?: boolean;
}

interface EntityRow {
  key: string;
  name: string;
  displayName?: string;
  description?: string;
  trackFirstLast?: boolean;
  displayOrder?: number;
  showInTable?: boolean;
}

interface SortableRowProps {
  entity: EntityRow;
  onEdit: (key: string, entity: EntityDefinition) => void;
  onDelete: (key: string) => void;
  onToggleShowInTable: (key: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ entity, onEdit, onDelete, onToggleShowInTable }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entity.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell width={50}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIcon fontSize="small" />
        </Box>
      </TableCell>
      <TableCell width={150}>
        <Chip
          label={entity.key}
          size="small"
          sx={{
            bgcolor: '#b8e6d5',
            color: '#1B0826',
            fontWeight: 500,
            fontFamily: 'monospace',
          }}
        />
      </TableCell>
      <TableCell width={200}>{entity.displayName}</TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {entity.description || '-'}
        </Typography>
      </TableCell>
      <TableCell width={140}>
        <Chip
          label={entity.trackFirstLast ? 'Yes' : 'No'}
          size="small"
          color={entity.trackFirstLast ? 'success' : 'default'}
        />
      </TableCell>
      <TableCell width={120} align="center">
        <Switch
          size="small"
          checked={entity.showInTable ?? true}
          onChange={() => onToggleShowInTable(entity.key)}
        />
      </TableCell>
      <TableCell width={100} align="right">
        <IconButton
          size="small"
          onClick={() => onEdit(entity.key, entity)}
          sx={{ mr: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(entity.key)}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

interface EntityDefinitionsEditorProps {
  entityDefinitions: Record<string, EntityDefinition> | undefined;
  onChange: (entityDefinitions: Record<string, EntityDefinition>) => void;
}

export const EntityDefinitionsEditor: React.FC<EntityDefinitionsEditorProps> = ({
  entityDefinitions = {},
  onChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<{ key: string; entity: EntityDefinition } | null>(null);
  const [formData, setFormData] = useState<EntityDefinition>({
    name: '',
    displayName: '',
    description: '',
    trackFirstLast: true,
    showInTable: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Sort entities by displayOrder for rendering
  const entities: EntityRow[] = Object.entries(entityDefinitions)
    .map(([key, value]) => ({ key, ...value }))
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = entities.findIndex((e) => e.key === active.id);
    const newIndex = entities.findIndex((e) => e.key === over.id);
    const reordered = arrayMove(entities, oldIndex, newIndex);

    // Rebuild with contiguous displayOrder
    const updated: Record<string, EntityDefinition> = {};
    reordered.forEach((entity, index) => {
      const { key, ...rest } = entity;
      updated[key] = { ...rest, displayOrder: index };
    });
    onChange(updated);
  };

  const handleAdd = () => {
    setEditingEntity(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      trackFirstLast: true,
      showInTable: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (key: string, entity: EntityDefinition) => {
    setEditingEntity({ key, entity });
    setFormData({
      ...entity,
      name: key,
      showInTable: entity.showInTable ?? true,
    });
    setDialogOpen(true);
  };

  const handleDelete = (key: string) => {
    const updated = { ...entityDefinitions };
    delete updated[key];
    // Re-normalize displayOrder
    const sorted = Object.entries(updated)
      .sort(([, a], [, b]) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    const renormalized: Record<string, EntityDefinition> = {};
    sorted.forEach(([k, v], index) => {
      renormalized[k] = { ...v, displayOrder: index };
    });
    onChange(renormalized);
  };

  const handleSave = () => {
    if (!formData.name) return;

    const updated = { ...entityDefinitions };
    const entityKey = editingEntity?.key || formData.name;

    // If renaming, delete old key
    if (editingEntity && editingEntity.key !== formData.name) {
      delete updated[editingEntity.key];
    }

    updated[entityKey] = {
      name: formData.name,
      displayName: formData.displayName || formData.name,
      description: formData.description,
      trackFirstLast: formData.trackFirstLast,
      displayOrder: editingEntity
        ? (entityDefinitions[editingEntity.key]?.displayOrder ?? entities.length)
        : entities.length,
      showInTable: formData.showInTable ?? true,
    };

    onChange(updated);
    setDialogOpen(false);
  };

  const handleToggleShowInTable = (key: string) => {
    const updated = { ...entityDefinitions };
    const current = updated[key].showInTable ?? true;
    updated[key] = { ...updated[key], showInTable: !current };
    onChange(updated);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Entity Definitions
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Define entities that will be tracked via SDK events. Drag to reorder priority.
          </Typography>
        </Box>
        <PrimaryButton startIcon={<AddIcon />} onClick={handleAdd}>
          Add Entity
        </PrimaryButton>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={50}></TableCell>
              <TableCell width={150}>Name</TableCell>
              <TableCell width={200}>Display Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width={140}>Track First/Last</TableCell>
              <TableCell width={120} align="center">Show in Table</TableCell>
              <TableCell width={100} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No entities defined. Click "Add Entity" to create one.
                </TableCell>
              </TableRow>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={entities.map((e) => e.key)}
                  strategy={verticalListSortingStrategy}
                >
                  {entities.map((entity) => (
                    <SortableRow
                      key={entity.key}
                      entity={entity}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleShowInTable={handleToggleShowInTable}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntity ? `Edit Entity: ${editingEntity.key}` : 'Add New Entity'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Entity Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., batch, order, product"
              helperText="Unique identifier (lowercase, no spaces). Used in SDK events."
              disabled={editingEntity !== null}
            />

            <TextField
              fullWidth
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Batch, Order, Product"
              helperText="Human-readable name shown in UI"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Inventory batches with expiry dates"
              multiline
              rows={2}
            />

            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Track First/Last Activity
              </Typography>
              <Box display="flex" gap={1}>
                <Chip
                  label="Yes"
                  onClick={() => setFormData({ ...formData, trackFirstLast: true })}
                  color={formData.trackFirstLast ? 'success' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="No"
                  onClick={() => setFormData({ ...formData, trackFirstLast: false })}
                  color={!formData.trackFirstLast ? 'default' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                If enabled, tracks firstAt, lastAt, and daysSinceLast for this entity
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Show in Accounts Table
              </Typography>
              <Box display="flex" gap={1}>
                <Chip
                  label="Yes"
                  onClick={() => setFormData({ ...formData, showInTable: true })}
                  color={formData.showInTable ? 'success' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="No"
                  onClick={() => setFormData({ ...formData, showInTable: false })}
                  color={!formData.showInTable ? 'default' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                If enabled, this entity appears as a column in the Accounts table
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setDialogOpen(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={!formData.name}>
            Save
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
